import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { apiErrorMessage } from '@shared/api/client';
import { uploadDocument } from '@shared/api/documents';
import type { DocumentKind, Uuid } from '@shared/types/api';

/**
 * Admin document upload. Server gates the actual rules (see
 * DocumentsService.assertCanUpload):
 *   APPROVED          → only FINAL is legal
 *   FINAL_DOC_READY   → any non-FINAL (DRAFT / SUPPORTING / REFERENCE)
 *   earlier states    → FINAL is blocked; other kinds per role rules
 *
 * The caller passes the set of kinds it wants to expose for the current
 * state so the picker can't offer something the server would reject.
 */
const KIND_LABELS: Record<DocumentKind, { title: string; hint: string }> = {
  SUPPORTING: { title: 'Supporting', hint: 'Client-facing evidence or reference material.' },
  DRAFT: { title: 'Draft', hint: 'Work-in-progress version (auto-versioned).' },
  FINAL: { title: 'Final document', hint: 'Closes the draft cycle.' },
  REFERENCE: { title: 'Reference', hint: 'Ancillary file (receipts, corrections, notes).' },
};

type Props = {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
  availableKinds: DocumentKind[];
  defaultKind?: DocumentKind;
  title?: string;
};

export function UploadFinalDialog({
  open,
  onClose,
  transactionId,
  availableKinds,
  defaultKind,
  title,
}: Props) {
  const [kind, setKind] = useState<DocumentKind>(
    defaultKind && availableKinds.includes(defaultKind) ? defaultKind : availableKinds[0],
  );
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const upload = useMutation({
    mutationFn: (f: File) => uploadDocument(transactionId, f, kind),
    onSuccess: () => {
      // A FINAL upload auto-advances APPROVED → FINAL_DOC_READY server-side.
      // Invalidate both the detail key and the admin tx list key so the
      // status badge refreshes without a manual reload.
      qc.invalidateQueries({ queryKey: ['admin', 'tx', 'one', transactionId] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
      qc.invalidateQueries({ queryKey: ['chat', transactionId, 'history'] });
      onClose();
      setFile(null);
    },
  });

  const singleKind = availableKinds.length === 1;
  const dialogTitle =
    title ??
    (singleKind && availableKinds[0] === 'FINAL' ? 'Upload final document' : 'Upload document');

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (upload.isPending) return;
        onClose();
        setFile(null);
      }}
      title={dialogTitle}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!file || upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </>
      }
    >
      {singleKind && availableKinds[0] === 'FINAL' ? (
        <p className="mb-3 text-xs text-gray-600">
          Once uploaded, the transaction moves to FINAL_DOC_READY. The 3-month support window
          starts when the client downloads the final document.
        </p>
      ) : null}

      {!singleKind ? (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700">Kind</div>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {availableKinds.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={cn(
                  'rounded-md border p-2 text-left text-xs',
                  kind === k
                    ? 'border-brand-600 bg-brand-50/40'
                    : 'border-gray-200 bg-white hover:bg-gray-50',
                )}
              >
                <div className="font-medium text-gray-900">{KIND_LABELS[k].title}</div>
                <div className="text-gray-500">{KIND_LABELS[k].hint}</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        onClick={() => fileRef.current?.click()}
        className={cn(
          'flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center',
          dragOver ? 'border-brand-600 bg-brand-50/40' : 'border-gray-300 bg-gray-50',
        )}
      >
        {file ? (
          <>
            <div className="text-sm font-medium text-gray-900">{file.name}</div>
            <div className="text-xs text-gray-500">
              {(file.size / 1024).toFixed(0)} KB · Click to replace
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-medium text-gray-900">
              Drop a file here or click to browse
            </div>
            <div className="text-xs text-gray-500">PDF, DOCX, XLSX, images — up to 50 MB</div>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setFile(f);
            e.target.value = '';
          }}
        />
      </div>
      {upload.isError ? (
        <p className="mt-2 text-sm text-red-600">
          {apiErrorMessage(upload.error, (upload.error as Error)?.message)}
        </p>
      ) : null}
    </Dialog>
  );
}
